import React, { useState } from 'react';
import {
  ActivityFeed,
  ActivityItem,
  AttentionMessage,
  Box,
  Link,
  Stack,
  Typography,
  SkeletonText,
  Container,
} from '@tq/ui-components';
import { useIsMobile, useThemedValueSwitch } from '@tq/ui-components/dist/hooks';
import { useTranslation } from 'react-i18next';

import { useFormatLocaleDate, StandardDateFormats } from 'config';
import { ConsultationNote } from 'types';
import { InternalExternalTag, NotesFilter, NotesFilterButtons } from './components';

type NotesProps = {
  notes?: ConsultationNote[];
  maxNotesBeforePaging?: number;
  loading?: boolean;
};

export const Notes: React.FC<NotesProps> = ({ notes, loading, maxNotesBeforePaging = 5 }) => {
  const { t } = useTranslation('notes');
  const { tvs } = useThemedValueSwitch();
  const [activeFilter, setActiveFilter] = useState<NotesFilter>('All');
  const formatLocaleDate = useFormatLocaleDate();

  const [isExpanded, setIsExpanded] = useState(false);
  const filteredNotes =
    notes?.filter((note) => note.noteType === activeFilter || activeFilter === 'All') || [];

  const sortNotes = (noteA: ConsultationNote, noteB: ConsultationNote) =>
    noteA.noteDate > noteB.noteDate ? -1 : 1;

  const sortedNotes = filteredNotes?.sort(sortNotes);

  const pagedNotes = sortedNotes?.slice(
    0,
    isExpanded ? sortedNotes.length + 1 : maxNotesBeforePaging
  );

  const isMobile = useIsMobile();

  if (loading) {
    return <SkeletonText h={400} noOfLines={15} />;
  }
  if (!notes?.length) {
    return <AttentionMessage title={t('no_notes', 'No notes to display.')} variant='inline' />;
  }

  // SQ made me put this here, think its much uglier but oh well.
  const activityFeedStyle = isMobile
    ? {
        height: '100%',
        maxHeight: '100%',
      }
    : undefined;

  return (
    <Stack spacing={4}>
      <NotesFilterButtons activeFilter={activeFilter} onChangeActiveFilter={setActiveFilter} />
      {!pagedNotes?.length && activeFilter === 'External' ? (
        <AttentionMessage
          title={t('external_noNotes', 'No external notes to display.')}
          variant='inline'
        />
      ) : null}
      {!pagedNotes?.length && activeFilter === 'Internal' ? (
        <AttentionMessage
          title={t('internal_noNotes', 'No internal notes to display.')}
          variant='inline'
        />
      ) : null}
      {pagedNotes?.length ? (
        <ActivityFeed heading='' sx={activityFeedStyle}>
          {pagedNotes?.map((note) => (
            <ActivityItem key={`${note.id}`}>
              <Box data-testid='note' maxWidth='100%' tabIndex={0}>
                <Stack direction='row' justifyContent='space-between'>
                  <Typography>
                    <h2 style={{ fontSize: '1rem' }}>
                      {note.consulteeName ?? note.caseOfficerName}
                    </h2>
                  </Typography>
                  <InternalExternalTag noteType={note.noteType} />
                </Stack>
                <Typography display='inline'>
                  <h3 style={{ fontSize: '0.875rem' }}>
                    {formatLocaleDate(note.noteDate, StandardDateFormats.dateAndTime)}
                  </h3>
                </Typography>
                <Typography mt={3} wordBreak='break-word'>
                  <p>{note.noteText}</p>
                </Typography>
              </Box>
            </ActivityItem>
          ))}
        </ActivityFeed>
      ) : null}
      {!isExpanded && filteredNotes.length > pagedNotes.length ? (
        <Container p={0}>
          <Box
            mx={tvs({ tq: '-1.25rem' })}
            mb={tvs({ tq: '-1.25rem' })}
            p={tvs({ tq: '1.25rem' })}
            bgColor={tvs({ tq: 'grey.50' })}
            pl={tvs({ ni: 3 })}
          >
            <Link
              bgColor={tvs({ tq: 'white' })}
              color={tvs({ tq: 'blue.275' })}
              p={tvs({ tq: '.3125rem 1.5625rem' })}
              onClick={() => setIsExpanded(true)}
            >
              {t('show_more', 'Show more')}
            </Link>
          </Box>
        </Container>
      ) : null}
    </Stack>
  );
};
